import { useState, useEffect, cloneElement, Children } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  ArrowRight, 
  Check, 
  ChevronDown, 
  ChevronUp,
  ChevronRight,
  Globe,
  Layout,
  Database,
  Wrench,
  Sparkles,
  Shield,
  Zap,
  Cpu,
  Users,
  Loader2,
  Home,
  Menu,
  X
} from "lucide-react";
import SiteWidget from "@/components/SiteWidget";
import NexusHeader from "@/components/NexusHeader";
import NexusFooter from "@/components/NexusFooter";
import { API_URL } from "../utils/api";
import { renderContentHtml } from "../utils/renderContentHtml";
import PremiumSection from '../sections/PremiumSection';
const { isPremiumSection } = require('../sections/catalog.cjs');

// Icon mapping (same as homepage)
const ICONS = {
  globe: Globe,
  layout: Layout,
  database: Database,
  tool: Wrench,
  sparkles: Sparkles,
  shield: Shield,
  zap: Zap,
  cpu: Cpu,
  users: Users
};

// Elido Logo
const ELIDO_LOGO = "https://customer-assets.emergentagent.com/job_custom-demo-gen/artifacts/x2wfas84_Elido_logotype_blue.png";

// Industries and cities for fallback generation with rich metadata
const INDUSTRIES = {
  'malare': { 
    name: 'Målare', 
    displayName: 'målerifirmor',
    benefits: ['Visa upp era måleriarbeten i portfolio', 'Få fler privatkundsuppdrag', 'Syns när kunder söker målare lokalt'],
    keywords: ['måleri', 'tapetsering', 'fasadmålning'],
    relatedIndustries: ['snickare', 'byggfirma', 'golvlaggare']
  },
  'elektriker': { 
    name: 'Elektriker', 
    displayName: 'elektriker',
    benefits: ['Få fler elinstallationsuppdrag', 'Bygg förtroende med certifieringar', 'Syns lokalt vid elsökningar'],
    keywords: ['elinstallation', 'elsäkerhet', 'belysning'],
    relatedIndustries: ['vvs', 'byggfirma', 'fastighetsservice']
  },
  'snickare': { 
    name: 'Snickare', 
    displayName: 'snickare',
    benefits: ['Visa upp snickeriarbeten i portfolio', 'Få fler renoveringsuppdrag', 'Locka kunder som söker kvalitetshantverk'],
    keywords: ['snickeri', 'träarbeten', 'renovering'],
    relatedIndustries: ['byggfirma', 'malare', 'golvlaggare']
  },
  'vvs': { 
    name: 'VVS', 
    displayName: 'VVS-företag',
    benefits: ['Visa upp era VVS-tjänster professionellt', 'Få fler förfrågningar från fastighetsägare', 'Syns när kunder söker rörmokare'],
    keywords: ['rörmokare', 'VVS-montör', 'värmesystem'],
    relatedIndustries: ['elektriker', 'byggfirma', 'dranering']
  },
  'byggfirma': { 
    name: 'Byggfirma', 
    displayName: 'byggfirmor',
    benefits: ['Presentera byggprojekt professionellt', 'Få fler offerförfrågningar', 'Bygg förtroende med referensprojekt'],
    keywords: ['byggföretag', 'entreprenad', 'nybyggnation'],
    relatedIndustries: ['snickare', 'malare', 'elektriker', 'byggservice']
  },
  'stadfirma': { 
    name: 'Städfirma', 
    displayName: 'städfirmor',
    benefits: ['Få fler återkommande städkunder', 'Visa upp era städtjänster tydligt', 'Locka privat- och företagskunder'],
    keywords: ['städning', 'kontorsstädning', 'hemstädning'],
    relatedIndustries: ['flyttfirma', 'fonsterputs', 'fastighetsservice']
  },
  'flyttfirma': { 
    name: 'Flyttfirma', 
    displayName: 'flyttfirmor',
    benefits: ['Få fler flyttbokningar online', 'Visa upp flytt-tjänster tydligt', 'Bygg förtroende med kundrecensioner'],
    keywords: ['flytt', 'bohagsflytt', 'kontorsflytt'],
    relatedIndustries: ['stadfirma', 'rivningsfirma']
  },
  'taklaggare': { 
    name: 'Takläggare', 
    displayName: 'takläggare',
    benefits: ['Få fler takrenoveringsuppdrag', 'Visa upp takreferenser', 'Locka fastighetsägare som söker takläggare'],
    keywords: ['takarbeten', 'takrenovering', 'plåttak'],
    relatedIndustries: ['byggfirma', 'snickare', 'murare']
  },
  'plattsattare': { 
    name: 'Plattsättare', 
    displayName: 'plattsättare',
    benefits: ['Visa upp plattarbeten i portfolio', 'Få fler badrumsrenoveringar', 'Syns vid sökningar på plattsättare'],
    keywords: ['kakel', 'klinker', 'badrumsrenovering'],
    relatedIndustries: ['vvs', 'snickare', 'golvlaggare']
  },
  'markentreprenad': { 
    name: 'Markentreprenad', 
    displayName: 'markentreprenörer',
    benefits: ['Presentera markarbetsprojekt', 'Få fler entreprenadförfrågningar', 'Syns vid sökningar på grävfirmor'],
    keywords: ['grävning', 'schaktning', 'markarbeten'],
    relatedIndustries: ['byggfirma', 'dranering', 'asfaltering']
  },
  'rivningsfirma': { 
    name: 'Rivningsfirma', 
    displayName: 'rivningsfirmor',
    benefits: ['Få fler rivningsuppdrag', 'Visa upp era rivningsprojekt', 'Bygg förtroende med referensprojekt'],
    keywords: ['rivning', 'demolering', 'sanering'],
    relatedIndustries: ['byggfirma', 'markentreprenad', 'flyttfirma']
  },
  'dranering': { 
    name: 'Dränering', 
    displayName: 'dräneringsfirmor',
    benefits: ['Få fler dräneringsuppdrag', 'Visa upp era dräneringsprojekt', 'Syns vid sökningar på dränering'],
    keywords: ['dränering', 'grundläggning', 'fuktproblem'],
    relatedIndustries: ['markentreprenad', 'vvs', 'byggfirma']
  },
  'golvlaggare': { 
    name: 'Golvläggare', 
    displayName: 'golvläggare',
    benefits: ['Visa upp golvprojekt i portfolio', 'Få fler golvuppdrag', 'Syns vid sökningar på golvläggare'],
    keywords: ['golvläggning', 'parkett', 'laminat'],
    relatedIndustries: ['snickare', 'malare', 'plattsattare']
  },
  'fastighetsservice': { 
    name: 'Fastighetsservice', 
    displayName: 'fastighetsserviceföretag',
    benefits: ['Få fler förvaltningsavtal', 'Visa upp era servicetjänster', 'Locka fastighetsägare och BRF:er'],
    keywords: ['fastighetsskötsel', 'underhåll', 'förvaltning'],
    relatedIndustries: ['stadfirma', 'elektriker', 'vvs']
  },
  'murare': { 
    name: 'Murare', 
    displayName: 'murare',
    benefits: ['Visa upp murararbeten i portfolio', 'Få fler murningsuppdrag', 'Syns vid sökningar på murare'],
    keywords: ['murning', 'tegelfasad', 'putsarbeten'],
    relatedIndustries: ['byggfirma', 'taklaggare', 'plattsattare']
  },
  'asfaltering': { 
    name: 'Asfaltering', 
    displayName: 'asfalteringsfirmor',
    benefits: ['Få fler asfalteringsuppdrag', 'Visa upp asfaltprojekt', 'Syns vid sökningar på asfaltering'],
    keywords: ['asfalt', 'beläggning', 'parkering'],
    relatedIndustries: ['markentreprenad', 'byggfirma']
  },
  'fonsterputs': { 
    name: 'Fönsterputs', 
    displayName: 'fönsterputsfirmor',
    benefits: ['Få fler återkommande kunder', 'Visa upp era putsprojekt', 'Locka företag och fastighetsägare'],
    keywords: ['fönsterputs', 'fönsterrengöring', 'höghusputsning'],
    relatedIndustries: ['stadfirma', 'fastighetsservice']
  },
  'bilverkstad': { 
    name: 'Bilverkstad', 
    displayName: 'bilverkstäder',
    benefits: ['Få fler bokningar online', 'Visa upp era tjänster tydligt', 'Bygg förtroende med kundrecensioner'],
    keywords: ['bilreparation', 'service', 'däckbyte'],
    relatedIndustries: []
  },
  'restaurang': { 
    name: 'Restaurang', 
    displayName: 'restauranger',
    benefits: ['Få fler gäster via online-synlighet', 'Visa upp er meny professionellt', 'Locka nya kunder i området'],
    keywords: ['mat', 'catering', 'lunch'],
    relatedIndustries: []
  },
  'byggservice': { 
    name: 'Byggservice', 
    displayName: 'byggservicefirmor',
    benefits: ['Få fler servicejobb', 'Visa upp era tjänster', 'Syns vid sökningar på byggservice'],
    keywords: ['byggservice', 'underhåll', 'reparationer'],
    relatedIndustries: ['byggfirma', 'snickare', 'fastighetsservice']
  },
};

const CITIES = {
  'malmo': { name: 'Malmö', region: 'Skåne' },
  'helsingborg': { name: 'Helsingborg', region: 'Skåne' },
  'lund': { name: 'Lund', region: 'Skåne' },
  'kristianstad': { name: 'Kristianstad', region: 'Skåne' },
  'halmstad': { name: 'Halmstad', region: 'Halland' },
  'angelholm': { name: 'Ängelholm', region: 'Skåne' },
  'landskrona': { name: 'Landskrona', region: 'Skåne' },
  'trelleborg': { name: 'Trelleborg', region: 'Skåne' },
  'ystad': { name: 'Ystad', region: 'Skåne' },
  'hassleholm': { name: 'Hässleholm', region: 'Skåne' },
  'jonkoping': { name: 'Jönköping', region: 'Småland' },
};

// Generate fallback page from slug with improved SEO content
function generateFallbackPage(slug) {
  const isHemsida = slug.startsWith('hemsida-');
  const isWebbdesign = slug.startsWith('webbdesign-');
  
  if (!isHemsida && !isWebbdesign) return null;
  
  const prefix = isHemsida ? 'hemsida-' : 'webbdesign-';
  const remainder = slug.replace(prefix, '');
  
  // Check for city suffix
  let matchedIndustry = null;
  let matchedCity = null;
  let citySlug = null;
  
  for (const [cSlug, cityData] of Object.entries(CITIES)) {
    if (remainder.endsWith(`-${cSlug}`)) {
      matchedCity = cityData;
      citySlug = cSlug;
      const industrySlug = remainder.replace(`-${cSlug}`, '');
      matchedIndustry = INDUSTRIES[industrySlug];
      if (matchedIndustry) {
        matchedIndustry = { ...matchedIndustry, slug: industrySlug };
      }
      break;
    }
  }
  
  // If no city, check for industry only
  if (!matchedIndustry) {
    matchedIndustry = INDUSTRIES[remainder];
    if (matchedIndustry) {
      matchedIndustry = { ...matchedIndustry, slug: remainder };
    }
  }
  
  // If no match, create generic from slug
  if (!matchedIndustry) {
    const parts = remainder.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1));
    matchedIndustry = {
      slug: remainder,
      name: parts.join(' '),
      displayName: parts.join(' ').toLowerCase(),
      benefits: ['Få fler kunder online', 'Professionell digital närvaro', 'SEO-optimerad hemsida'],
      keywords: [],
      relatedIndustries: []
    };
  }
  
  const cityText = matchedCity ? ` i ${matchedCity.name}` : '';
  const ctaText = 'Se din demo på 30 sek';
  
  // Conversion-focused title
  const title = isHemsida 
    ? `Hemsida för ${matchedIndustry.name}${cityText} – Få fler kunder`
    : `Webbdesign för ${matchedIndustry.name}${cityText} – Konverterande design`;
  
  // Benefit-driven description with CTA
  const description = isHemsida
    ? (matchedCity 
        ? `Professionell hemsida för ${matchedIndustry.displayName}${cityText}. Vi hjälper er synas på Google och få fler förfrågningar lokalt. ✓ Gratis demo ✓ Ingen bindningstid.`
        : `Modern hemsida för ${matchedIndustry.displayName} som genererar fler förfrågningar. ✓ SEO-optimerad ✓ Mobilanpassad ✓ Resultatfokuserad. Testa gratis!`)
    : (matchedCity
        ? `Skräddarsydd webbdesign för ${matchedIndustry.displayName}${cityText}. Vi skapar hemsidor som förvandlar besökare till kunder. ✓ Lokal SEO ✓ Modern design.`
        : `Professionell webbdesign för ${matchedIndustry.displayName}. Hemsidor som bygger förtroende och genererar leads. ✓ Responsiv ✓ Snabb ✓ SEO-optimerad.`);
  
  // Build related industries links
  const relatedLinks = (matchedIndustry.relatedIndustries || [])
    .slice(0, 3)
    .map(relSlug => {
      const relInd = INDUSTRIES[relSlug];
      return relInd ? { slug: relSlug, name: relInd.name } : null;
    })
    .filter(Boolean);
  
  // Build city links
  const cityLinks = matchedCity 
    ? Object.entries(CITIES)
        .filter(([cSlug]) => cSlug !== citySlug)
        .slice(0, 4)
        .map(([cSlug, cData]) => ({ slug: cSlug, name: cData.name }))
    : Object.entries(CITIES)
        .slice(0, 5)
        .map(([cSlug, cData]) => ({ slug: cSlug, name: cData.name }));
  
  return {
    title,
    meta_title: `${title} | Elido`,
    meta_description: description,
    slug,
    industry: matchedIndustry,
    city: matchedCity,
    citySlug,
    isHemsida,
    relatedLinks,
    cityLinks,
    sections: [
      {
        type: 'hero',
        headline: title,
        subheadline: description,
        cta_primary: { text: `Gratis demo →`, link: '/gratis-demo' },
        cta_secondary: { text: 'Kontakta oss', link: '/#kontakt' },
        visible: true
      },
      {
        type: 'benefits',
        title: `Varför behöver ${matchedIndustry.displayName} en professionell hemsida?`,
        subtitle: matchedCity 
          ? `Som ${matchedIndustry.displayName.slice(0, -2) || matchedIndustry.displayName}${cityText} konkurrerar ni med många andra företag. En professionell hemsida är avgörande för att synas.`
          : `Allt fler kunder söker efter ${matchedIndustry.displayName} online. Utan en professionell hemsida riskerar ni att gå miste om värdefulla uppdrag.`,
        items: [
          ...(matchedIndustry.benefits || []).map((benefit, i) => ({
            title: benefit.split(' ').slice(0, 3).join(' '),
            description: benefit,
            icon: ['layout', 'sparkles', 'globe', 'zap'][i] || 'check'
          })),
          { title: 'Spara tid', description: 'Automatiserade kontaktformulär', icon: 'shield' },
        ],
        visible: true
      },
      {
        type: 'benefits',
        title: 'Vad ingår i er nya hemsida?',
        subtitle: `Vi bygger hemsidor specifikt anpassade för ${matchedIndustry.displayName}.`,
        items: [
          { title: 'Branschanpassad design', description: `Professionellt utseende för ${matchedIndustry.displayName}`, icon: 'layout' },
          { title: 'Mobiloptimerad', description: 'Perfekt på mobil, surfplatta och dator', icon: 'globe' },
          { title: 'Google-optimerad', description: `SEO så ni syns högre${cityText}`, icon: 'sparkles' },
          { title: 'Snabba laddtider', description: 'Under 2 sekunder för bästa upplevelse', icon: 'zap' },
          { title: 'Kontaktformulär', description: 'Enkelt för kunder att skicka förfrågningar', icon: 'users' },
          { title: 'SSL-certifikat', description: 'Säker anslutning som bygger förtroende', icon: 'shield' },
        ],
        visible: true
      },
      ...(matchedCity ? [{
        type: 'text',
        title: `Lokal SEO för ${matchedIndustry.displayName} i ${matchedCity.name}`,
        content: `Vi specialiserar oss på lokal SEO för företag i ${matchedCity.name}. Det innebär att vi optimerar er hemsida så att den syns när kunder i ${matchedCity.name} och ${matchedCity.region || 'närliggande områden'} söker efter ${matchedIndustry.displayName}.\n\nLokal SEO inkluderar optimering för "nära mig"-sökningar, Google My Business-integrering, lokala sökord som "${matchedIndustry.name} ${matchedCity.name}", och strukturerad data för lokala företag.`,
        visible: true
      }] : []),
      {
        type: 'benefits',
        title: 'Varför välja Elido?',
        subtitle: 'Vi har hjälpt över 100 företag att växa online.',
        items: [
          { title: 'Över 100 nöjda kunder', description: 'Vi vet vad som fungerar', icon: 'users' },
          { title: 'Ingen bindningstid', description: 'Ni väljer själva hur länge', icon: 'shield' },
          { title: 'Support på svenska', description: 'Vi finns här när ni behöver hjälp', icon: 'globe' },
          { title: 'Gratis demo', description: 'Se hur er hemsida kan se ut först', icon: 'sparkles' },
        ],
        visible: true
      },
      {
        type: 'cta',
        title: 'Se hur er nya hemsida kan se ut – gratis',
        subtitle: `Testa vår gratis demo och få en förhandsvisning av hur er nya hemsida kan se ut. Det tar bara 30 sekunder och kräver ingen betalning eller bindning.`,
        cta_text: 'Skapa gratis demo nu →',
        cta_link: '/gratis-demo',
        visible: true
      }
    ]
  };
}

/**
 * AmbientOrbs — soft, brand-tinted radial glows positioned absolutely
 * behind a section's content. Three variants rotate through so consecutive
 * sections don't look identical. The orbs use `mix-blend-mode: screen` so
 * they brighten dark backgrounds without making white cards look weird.
 * `pointer-events:none` keeps them invisible to mouse/keyboard.
 */
function AmbientOrbs({ variant = 0 }) {
  const layouts = [
    // Top-right + bottom-left
    [
      { top: "-10%", right: "-8%", size: "55vw", color: "rgba(58,138,152,0.75)", delay: "0s" },
      { bottom: "-20%", left: "-10%", size: "45vw", color: "rgba(127,212,222,0.55)", delay: "-6s" },
    ],
    // Top-left + mid-right
    [
      { top: "-15%", left: "-12%", size: "50vw", color: "rgba(127,212,222,0.60)", delay: "-3s" },
      { top: "30%", right: "-15%", size: "60vw", color: "rgba(37,94,105,0.85)", delay: "-9s" },
    ],
    // Center wide + bottom-right
    [
      { top: "10%", left: "20%", size: "60vw", color: "rgba(58,138,152,0.55)", delay: "-5s" },
      { bottom: "-15%", right: "-12%", size: "45vw", color: "rgba(127,212,222,0.55)", delay: "-11s" },
    ],
  ];
  const orbs = layouts[variant] || layouts[0];
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
      style={{ zIndex: 0 }}
    >
      <style>{`
        @keyframes elidoAmbientDrift {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50%      { transform: translate3d(2vw, -2vw, 0) scale(1.06); }
        }
        @media (prefers-reduced-motion: reduce) {
          .elido-orb { animation: none !important; }
        }
      `}</style>
      {orbs.map((o, i) => (
        <div
          key={i}
          className="elido-orb absolute rounded-full"
          style={{
            top: o.top,
            bottom: o.bottom,
            left: o.left,
            right: o.right,
            width: o.size,
            height: o.size,
            background: `radial-gradient(closest-side, ${o.color}, transparent 70%)`,
            filter: "blur(55px)",
            mixBlendMode: "screen",
            opacity: 0.95,
            animation: "elidoAmbientDrift 24s ease-in-out infinite",
            animationDelay: o.delay,
            willChange: "transform",
          }}
        />
      ))}
    </div>
  );
}

// Admin-authored standalone SEO/case pages already carry their own full-width
// layout, responsive spacing and styles. Do not wrap those pages in the
// generic padded/narrow `text` block (which creates a 96px black band above
// the hero on desktop, e.g. /hemsida-malmo).
function isStandaloneStyledSeoSection(section) {
  const html = section?.content;
  return section?.type === "text"
    && !String(section?.title || "").trim()
    && typeof html === "string"
    && /^\s*<style\b/i.test(html)
    && /<section\b/i.test(html)
    && /width\s*:\s*100vw/i.test(html);
}

export default function SeoPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatbotSettings, setChatbotSettings] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // v3.58: reset state on every slug change so the previous page's
    // hero/sections don't briefly remain visible while the new slug
    // is being fetched, and so a stale error from a prior 404 can't
    // mask a successful response for the new slug.
    setPage(null);
    setError(null);
    setLoading(true);

    loadPage();
    loadChatbotSettings();

    // Scroll listener for sticky header
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const redirectIfConfigured = async (s) => {
    // Honor an SEO lifecycle redirect (retired/merged slug) BEFORE any
    // fallback page can be generated/rendered. Fails open (returns false)
    // on any error so normal fallback still works for non-redirect slugs.
    try {
      const r = await fetch(`${API_URL}/api/seo/resolve?path=/${encodeURIComponent(s)}`);
      if (!r.ok) return false;
      const d = await r.json();
      if (d && d.action === "redirect" && typeof d.redirect_to === "string" && d.redirect_to.startsWith("/")) {
        window.location.replace(d.redirect_to);
        return true;
      }
    } catch (e) {
      /* fail open */
    }
    return false;
  };

  const loadPage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/seo-pages/${slug}`);
      if (!response.ok) {
        // A retired/merged slug must redirect, never render a fallback.
        if (await redirectIfConfigured(slug)) return;
        // Try fallback generation for hemsida-* and webbdesign-* slugs
        const fallbackPage = generateFallbackPage(slug);
        if (fallbackPage) {
          setPage(fallbackPage);
          setLoading(false);
          return;
        }
        throw new Error("Page not found");
      }
      const data = await response.json();
      setPage(data.page);
    } catch (err) {
      // A retired/merged slug must redirect, never render a fallback.
      if (await redirectIfConfigured(slug)) return;
      // Try fallback generation
      const fallbackPage = generateFallbackPage(slug);
      if (fallbackPage) {
        setPage(fallbackPage);
      } else {
        // v3.58: explicit error message instead of silent null — the
        // render path below shows a visible "Sidan hittades inte"
        // panel inside NexusLayout, never a blank screen.
        setError("Sidan kunde inte hittas");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadChatbotSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/website/chatbot-settings`);
      if (response.ok) {
        const data = await response.json();
        setChatbotSettings(data.settings);
      }
    } catch (error) {
      console.error("Failed to load chatbot settings");
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Handle CTA link clicks - navigate to homepage with hash
  const handleCtaClick = (e, link) => {
    if (!link) return;
    
    // Handle chatbot links
    if (link === "#chatbot" || link === "#chatbot-lead") {
      e.preventDefault();
      if (link === "#chatbot-lead") {
        window.dispatchEvent(new CustomEvent("openChatbotLead"));
      } else {
        window.dispatchEvent(new CustomEvent("openChatbot"));
      }
      return;
    }
    
    // Handle hash links like /#kontakt, /#priser
    if (link.startsWith("/#") || link.startsWith("#")) {
      e.preventDefault();
      const hash = link.replace("/", "");
      // Navigate to homepage with hash
      window.location.href = "/" + hash;
      return;
    }
  };

  if (loading) {
    return (
      <div className="elido-delayed-public-loader bg-[#0a1418] flex items-center justify-center min-h-[60vh]" data-testid="seo-page-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#3A8A98]" />
      </div>
    );
  }

  if (error || !page) {
    // v3.58: rendered INSIDE NexusLayout so header/footer/widget stay
    // visible. Never a blank page — the user always sees branding and
    // a clear path back. Crawlers that hit a slug without backend data
    // see this same friendly 404 instead of an empty body.
    return (
      <div className="bg-[#0a1418] flex flex-col items-center justify-center text-center px-6 py-24 min-h-[60vh]" data-testid="seo-page-not-found">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-rose-500/10 text-rose-400 border border-rose-500/30 mb-5">
          404 — sidan saknas
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
          Sidan hittades inte
        </h1>
        <p className="text-base text-white/60 max-w-md mb-8">
          Adressen <code className="font-mono text-white/80">/{slug}</code> matchar ingen aktiv sida. Den kan vara borttagen eller flyttad.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/" className="px-5 py-2.5 rounded-full bg-[#255E69] hover:bg-[#1f4d57] text-white font-medium transition-colors" data-testid="seo-404-home-link">
            Tillbaka till startsidan
          </Link>
          <Link to="/skapa-ai-agent" className="px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/5 text-white font-medium transition-colors" data-testid="seo-404-create-link">
            Skapa AI-agent
          </Link>
        </div>
      </div>
    );
  }

  // Render different section types
  const renderSection = (section, index) => {
    if (isPremiumSection(section)) {
      if (section.visible === false) return null;
      const earlierHeading = (page.sections || []).slice(0, index).some(s => s.visible !== false && (s.type === 'hero' || /<h1\b/i.test(s.content || '')));
      return <PremiumSection key={index} section={section} sectionIndex={index} headingTag={earlierHeading ? 'h2' : 'h1'} />;
    }
    if (!section.visible && section.visible !== undefined) return null;

    switch (section.type) {
      case "hero":
        // Build breadcrumb items
        const breadcrumbItems = [];
        breadcrumbItems.push({ label: 'Hem', href: '/', icon: Home });
        
        if (page.isHemsida !== undefined) {
          const prefix = page.isHemsida ? 'hemsida' : 'webbdesign';
          const prefixLabel = page.isHemsida ? 'Hemsida' : 'Webbdesign';
          breadcrumbItems.push({ label: prefixLabel, href: '/#tjanster' });
          
          if (page.industry) {
            if (page.city) {
              // City page: show industry as link
              breadcrumbItems.push({ 
                label: page.industry.name, 
                href: `/${prefix}-${page.industry.slug}` 
              });
              breadcrumbItems.push({ label: page.city.name, current: true });
            } else {
              // National page: industry is current
              breadcrumbItems.push({ label: page.industry.name, current: true });
            }
          }
        }
        
        return (
          <section key={index} className="relative bg-[#0F2A30] pt-20 pb-16 lg:py-32">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-20 right-20 w-96 h-96 bg-[#3A8A98] rounded-full blur-3xl"></div>
            </div>
            <div className="relative max-w-7xl mx-auto px-6">
              {/* Breadcrumbs */}
              {breadcrumbItems.length > 1 && (
                <nav aria-label="Breadcrumb" className="mb-8">
                  <ol className="flex flex-wrap items-center gap-1 text-sm">
                    {breadcrumbItems.map((item, i) => (
                      <li key={i} className="flex items-center">
                        {i > 0 && (
                          <ChevronRight className="w-4 h-4 text-gray-500 mx-1" />
                        )}
                        {item.current ? (
                          <span className="text-gray-300" aria-current="page">
                            {item.label}
                          </span>
                        ) : (
                          <Link 
                            to={item.href} 
                            className="text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                            onClick={(e) => item.href.startsWith('/#') && handleCtaClick(e, item.href)}
                          >
                            {item.icon && <item.icon className="w-3.5 h-3.5" />}
                            {item.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ol>
                </nav>
              )}
              
              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Text content - shown first on mobile */}
                <div className="text-center lg:text-left">
                  <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
                    {section.headline}
                  </h1>
                  <p className="text-base md:text-lg lg:text-xl text-gray-300 mb-8 lg:mb-10 leading-relaxed hidden lg:block">
                    {section.subheadline}
                  </p>
                  <div className="hidden lg:flex flex-col sm:flex-row gap-4">
                    {section.cta_primary?.text && (
                      <a
                        href={section.cta_primary.link || "/#kontakt"}
                        onClick={(e) => handleCtaClick(e, section.cta_primary.link || "/#kontakt")}
                        className="bg-gradient-to-r from-[#255E69] to-[#3A8A98] text-white px-8 py-4 rounded-xl text-lg font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                      >
                        {section.cta_primary.text}
                        <ArrowRight className="w-5 h-5" />
                      </a>
                    )}
                    {section.cta_secondary?.text && (
                      <a
                        href={section.cta_secondary.link || "/"}
                        onClick={(e) => handleCtaClick(e, section.cta_secondary.link || "/")}
                        className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl text-lg font-medium hover:bg-white/20 transition-colors border border-white/20"
                      >
                        {section.cta_secondary.text}
                      </a>
                    )}
                  </div>
                </div>
                
                {/* Hero Image */}
                {(section.hero_image || section.image) && (
                  <div className="w-full">
                    <img 
                      src={section.hero_image || section.image} 
                      alt={section.image_alt || section.headline || ""} 
                      className="rounded-2xl shadow-2xl w-full max-w-sm mx-auto lg:max-w-none"
                    />
                  </div>
                )}
                
                {/* Mobile-only: Subheadline and CTAs below image */}
                <div className="lg:hidden text-center w-full">
                  <p className="text-base text-gray-300 mb-6 leading-relaxed">
                    {section.subheadline}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    {section.cta_primary?.text && (
                      <a
                        href={section.cta_primary.link || "/#kontakt"}
                        onClick={(e) => handleCtaClick(e, section.cta_primary.link || "/#kontakt")}
                        className="bg-gradient-to-r from-[#255E69] to-[#3A8A98] text-white px-8 py-4 rounded-xl text-lg font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                      >
                        {section.cta_primary.text}
                        <ArrowRight className="w-5 h-5" />
                      </a>
                    )}
                    {section.cta_secondary?.text && (
                      <a
                        href={section.cta_secondary.link || "/"}
                        onClick={(e) => handleCtaClick(e, section.cta_secondary.link || "/")}
                        className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl text-lg font-medium hover:bg-white/20 transition-colors border border-white/20"
                      >
                        {section.cta_secondary.text}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );

      case "text":
        if (isStandaloneStyledSeoSection(section)) {
          // Keep the authored design edge-to-edge, immediately below the
          // shared header. Plain text sections still use the padded template.
          return (
            <section key={index} className="bg-[#0a1418]" data-testid="seo-standalone-layout">
              <div dangerouslySetInnerHTML={{ __html: renderContentHtml(section.content) }} />
            </section>
          );
        }
        return (
          <section key={index} className="py-16 lg:py-24 bg-[#0a1418]">
            <div className="max-w-4xl mx-auto px-6">
              {section.title && (
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 tracking-tight">
                  {section.title}
                </h2>
              )}
              <div 
                className="prose prose-lg prose-invert max-w-none text-white/70 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderContentHtml(section.content) }}
              />
            </div>
          </section>
        );

      case "benefits":
        return (
          <section key={index} className="py-16 lg:py-24 bg-[#081016] border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-12">
                {section.title && (
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
                    {section.title}
                  </h2>
                )}
                {section.subtitle && (
                  <p className="text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
                    {section.subtitle}
                  </p>
                )}
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {(section.items || []).map((item, i) => {
                  const Icon = ICONS[item.icon] || Check;
                  return (
                    <div key={i} className="bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 rounded-2xl p-6 hover:border-[#3A8A98]/40 transition-all duration-300">
                      <div className="w-12 h-12 rounded-xl bg-[#3A8A98]/15 border border-[#3A8A98]/30 flex items-center justify-center mb-5">
                        <Icon className="w-6 h-6 text-[#5DBCCB]" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );

      case "faq":
        return (
          <section key={index} className="py-16 lg:py-24 bg-[#0a1418] border-t border-white/5">
            <div className="max-w-3xl mx-auto px-6">
              {section.title && (
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center tracking-tight">
                  {section.title}
                </h2>
              )}
              <div className="space-y-3">
                {(section.items || []).map((item, i) => (
                  <div key={i} className="border border-white/10 rounded-xl overflow-hidden bg-white/[0.02]">
                    <button
                      onClick={() => toggleFaq(`${index}-${i}`)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.04] transition-colors"
                    >
                      <span className="font-semibold text-white">{item.question}</span>
                      {expandedFaq[`${index}-${i}`] ? (
                        <ChevronUp className="w-5 h-5 text-[#5DBCCB]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-white/40" />
                      )}
                    </button>
                    {expandedFaq[`${index}-${i}`] && (
                      <div className="px-5 pb-5 text-white/70 leading-relaxed">
                        {item.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case "cta":
        return (
          <section key={index} className="py-16 lg:py-24 bg-[#081016] border-t border-white/5 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-60"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(58,138,152,0.18) 0%, transparent 60%)",
              }}
              aria-hidden="true"
            />
            <div className="relative max-w-4xl mx-auto px-6 text-center">
              {section.title && (
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
                  {section.title}
                </h2>
              )}
              {section.subtitle && (
                <p className="text-xl text-white/60 mb-8 leading-relaxed">
                  {section.subtitle}
                </p>
              )}
              {section.cta_text && (
                <a
                  href={section.cta_link || "/#kontakt"}
                  onClick={(e) => handleCtaClick(e, section.cta_link || "/#kontakt")}
                  className="inline-flex items-center gap-2 bg-[#3A8A98] hover:bg-[#2c7a87] text-white px-8 py-4 rounded-full text-base font-semibold transition-all duration-300 hover:translate-y-[-1px] shadow-lg shadow-[#3A8A98]/20"
                >
                  {section.cta_text}
                  <ArrowRight className="w-5 h-5" />
                </a>
              )}
            </div>
          </section>
        );

      case "image_text":
        return (
          <section key={index} className="py-16 lg:py-24 bg-[#0a1418] border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6">
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${section.image_position === 'left' ? 'lg:flex-row-reverse' : ''}`}>
                {section.image_position === 'left' && section.image && (
                  <div>
                    <img
                      src={section.image}
                      alt={section.image_alt || ""}
                      className="rounded-2xl border border-white/10 shadow-2xl shadow-black/40"
                    />
                  </div>
                )}
                <div>
                  {section.title && (
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight">
                      {section.title}
                    </h2>
                  )}
                  <div
                    className="prose prose-lg prose-invert text-white/70 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: renderContentHtml(section.content) }}
                  />
                </div>
                {section.image_position !== 'left' && section.image && (
                  <div>
                    <img
                      src={section.image}
                      alt={section.image_alt || ""}
                      className="rounded-2xl border border-white/10 shadow-2xl shadow-black/40"
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        );

      case "pricing_preview":
        return (
          <section key={index} className="py-16 lg:py-24 bg-[#081016] border-t border-white/5">
            <div className="max-w-4xl mx-auto px-6 text-center">
              {section.title && (
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
                  {section.title}
                </h2>
              )}
              {section.subtitle && (
                <p className="text-white/60 text-lg mb-8 leading-relaxed">
                  {section.subtitle}
                </p>
              )}
              <a
                href="/#priser"
                onClick={(e) => handleCtaClick(e, "/#priser")}
                className="inline-flex items-center gap-2 bg-[#3A8A98] hover:bg-[#2c7a87] text-white px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:translate-y-[-1px] shadow-lg shadow-[#3A8A98]/20"
              >
                Se våra paket
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </section>
        );

      default:
        return null;
    }
  };

  // Ensure title is always a non-empty string for Helmet
  const pageTitle = page.meta_title || page.title || "Elido";
  const pageDescription = page.meta_description || "";
  
  // Generate BreadcrumbList schema
  const generateBreadcrumbSchema = () => {
    if (page.isHemsida === undefined) return null;
    
    const prefix = page.isHemsida ? 'hemsida' : 'webbdesign';
    const prefixLabel = page.isHemsida ? 'Hemsida' : 'Webbdesign';
    
    const items = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Hem",
        "item": "https://elido.nu"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": prefixLabel,
        "item": "https://elido.nu/#tjanster"
      }
    ];
    
    if (page.industry) {
      if (page.city) {
        items.push({
          "@type": "ListItem",
          "position": 3,
          "name": page.industry.name,
          "item": `https://elido.nu/${prefix}-${page.industry.slug}`
        });
        items.push({
          "@type": "ListItem",
          "position": 4,
          "name": page.city.name,
          "item": `https://elido.nu/${page.slug}`
        });
      } else {
        items.push({
          "@type": "ListItem",
          "position": 3,
          "name": page.industry.name,
          "item": `https://elido.nu/${page.slug}`
        });
      }
    }
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items
    };
  };
  
  const breadcrumbSchema = generateBreadcrumbSchema();

  return (
    <>
      {/* SEO head ownership: SeoPage owns its description and may override
          robots for a DB-backed noindex page. All other routes still use
          GlobalSEOHead as the sole robots/canonical owner. The merged Helmet
          deduplicates matching meta names on hydration. */}
      <Helmet
        title={pageTitle}
        meta={[
          { name: "description", content: pageDescription },
          ...((page.status === "noindex" || page.noindex === true)
            ? [{ name: "robots", content: "noindex,nofollow" }]
            : [])
        ]}
      >
        {breadcrumbSchema && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        )}
      </Helmet>

      {/* v3.58: key={slug} forces a clean remount of the content tree on
          slug change. Belt-and-braces with the per-effect state reset
          above — guarantees no stale section/hero DOM from the previous
          page ever survives a SPA navigation. */}
      <div className="relative" key={slug}>
        {/* Header is provided by NexusLayout */}

        {/* Sections — each one gets a soft brand-tinted ambient orb backdrop
            so the dark page never feels like a flat black void. The hero
            already has its own decoration so we skip it to avoid doubling. */}
        {(page.sections || []).map((section, index) => {
          const rendered = renderSection(section, index);
          if (!rendered || section.type === "hero" || isPremiumSection(section) || isStandaloneStyledSeoSection(section)) return rendered;
          const prevCls = rendered.props.className || "";
          const cls = prevCls.includes("relative")
            ? prevCls + " overflow-hidden"
            : prevCls + " relative overflow-hidden";
          const kids = Children.toArray(rendered.props.children);
          return cloneElement(rendered, {
            className: cls,
            children: [<AmbientOrbs key="elido-ambient" variant={index % 3} />, ...kids],
          });
        })}

        {/* Internal linking section for fallback pages */}
        {page.relatedLinks && page.relatedLinks.length > 0 && (
          <section className="py-16 bg-[#081016] border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Related industries */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Hemsidor för andra branscher</h3>
                  <ul className="space-y-2">
                    {page.relatedLinks.map((rel, i) => (
                      <li key={i}>
                        <Link 
                          to={`/${page.isHemsida ? 'hemsida' : 'webbdesign'}-${rel.slug}`}
                          className="text-[#5DBCCB] hover:text-white transition-colors"
                        >
                          Hemsida för {rel.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* City links */}
                {page.cityLinks && page.cityLinks.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">
                      {page.city ? `${page.industry?.name || 'Hemsidor'} i andra städer` : `${page.industry?.name || 'Hemsidor'} per stad`}
                    </h3>
                    <ul className="space-y-2">
                      {page.cityLinks.map((cityLink, i) => (
                        <li key={i}>
                          <Link 
                            to={`/${page.isHemsida ? 'hemsida' : 'webbdesign'}-${page.industry?.slug || 'foretag'}-${cityLink.slug}`}
                            className="text-[#5DBCCB] hover:text-white transition-colors"
                          >
                            Hemsida för {page.industry?.name || 'företag'} i {cityLink.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Main navigation */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Utforska Elido</h3>
                  <ul className="space-y-2">
                    <li>
                      <Link to="/" className="text-[#5DBCCB] hover:text-white transition-colors">
                        Startsida – Hemsidor för företag
                      </Link>
                    </li>
                    <li>
                      <Link to="/gratis-demo" className="text-[#5DBCCB] hover:text-white transition-colors font-medium">
                        Gratis demo – Se din hemsida på 30 sek →
                      </Link>
                    </li>
                    <li>
                      <Link to="/om-oss" className="text-[#255E69] hover:text-[#1e4a53] hover:underline">
                        Om Elido AB
                      </Link>
                    </li>
                    <li>
                      <Link to="/blog" className="text-[#5DBCCB] hover:text-white transition-colors">
                        Blogg – Tips om hemsidor
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Footer + widget provided by NexusLayout */}
      </div>
    </>
  );
}
